/* eslint-disable jsx-a11y/no-autofocus */
import { RAuditConfig } from '@counsel-project/counsel-audit-backend-api'
import CheckIcon from '@mui/icons-material/CheckRounded'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/EditRounded'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import MeasureTable from './MeasureTable'
import ProgramSelector from './ProgramSelector'
import Stack from '@mui/material/Stack'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControl from '@mui/material/FormControl'
import LocationSelector from './LocationSelector'

type AuditConfigEditProps = {
  auditConfig: RAuditConfig
  isNew?: boolean
  hasUnsavedChanges?: boolean
  onChangeAuditConfig: (value: RAuditConfig) => void
  onDelete?: () => void
}

const AuditConfigEdit = ({
  auditConfig,
  isNew,
  hasUnsavedChanges,
  onChangeAuditConfig,
  onDelete,
}: AuditConfigEditProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingTitle, setEditingTitle] = useState(isNew)

  const handleDeleteConfig = () => {
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete()
    }
    setDeleteDialogOpen(false)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
  }

  const handleSaveTitle = () => {
    onChangeAuditConfig({ ...auditConfig, title: auditConfig.title })
    setEditingTitle(false)
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: hasUnsavedChanges ? 'warning.light' : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        {editingTitle ? (
          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                size="small"
                value={auditConfig.title}
                onChange={(e) => onChangeAuditConfig({ ...auditConfig, title: e.target.value })}
                fullWidth
                inputProps={{ 'data-qa-id': 'config-name-input' }}
              />
            </Grid>
            <Grid item>
              <IconButton
                color="primary"
                onClick={handleSaveTitle}
                size="small"
                data-qa-id="config-confirm-name-button"
              >
                <CheckIcon />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" onClick={() => setEditingTitle(true)}>
              {auditConfig.title}
            </Typography>
            <IconButton
              onClick={() => setEditingTitle(true)}
              sx={{ ml: 1 }}
              size="small"
              data-qa-id="config-edit-name-button"
            >
              <EditIcon />
            </IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {hasUnsavedChanges && <Chip label="Unsaved Changes" color="warning" size="small" />}
          {onDelete && (
            <IconButton onClick={handleDeleteConfig} data-qa-id="config-delete-button">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" paragraph>
          Select programs to check compliance for
        </Typography>
        <ProgramSelector
          value={auditConfig.programs}
          onChange={(programs) => onChangeAuditConfig({ ...auditConfig, programs })}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" paragraph>
          Select locations to check compliance for
        </Typography>
        <LocationSelector
          value={auditConfig.locations ?? []}
          onChange={(locations) => onChangeAuditConfig({ ...auditConfig, locations })}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Real-time compliance configuration
        </Typography>
        <Stack spacing={3} mt={3}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={auditConfig.displayComplianceCheckButton}
                  onChange={(e) =>
                    onChangeAuditConfig({
                      ...auditConfig,
                      displayComplianceCheckButton: e.target.checked,
                    })
                  }
                />
              }
              label="Display the compliance check button?"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Show a button in the UI for clinicians to manually trigger a compliance audit.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={auditConfig.autoComplianceCheck}
                  onChange={(e) =>
                    onChangeAuditConfig({
                      ...auditConfig,
                      autoComplianceCheck: e.target.checked,
                    })
                  }
                />
              }
              label="Automatically check compliance when document/note is submitted"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Enable automatic compliance checks on document/note submission.
            </Typography>
          </Box>

          <Typography variant="body1" color="text.primary">
            Minimum severity level for real-time alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the minimum severity level for alerts to be shown to clinicians in real-time.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={auditConfig.minimumSeverityForRealTimeAlerts}
              onChange={(e) =>
                onChangeAuditConfig({
                  ...auditConfig,
                  minimumSeverityForRealTimeAlerts: parseInt(e.target.value) as 1 | 2 | 3 | -1,
                })
              }
            >
              <FormControlLabel value={1} control={<Radio />} label="Low" />
              <FormControlLabel value={2} control={<Radio />} label="Medium" />
              <FormControlLabel value={3} control={<Radio />} label="Severe" />
              <FormControlLabel value={-1} control={<Radio />} label="Never" />
            </RadioGroup>
          </FormControl>

          {/* NON-MVP: Treatment Plan Signature Requirements
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              For Treatment Plans: <span style={{ color: 'red' }}>NON-MVP</span>
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="body2">When do treatment plans need to be signed by?</Typography>
              <TextField
                value={treatmentPlanTime}
                onChange={e => setTreatmentPlanTime(e.target.value)}
                size="small"
                sx={{ width: 100 }}
                helperText={<span style={{ color: 'red' }}>NON-MVP</span>}
              />
            </Stack>
          </Box>
          */}

          {/* NON-MVP: Progress Note Signature Requirements
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              For Progress Notes: <span style={{ color: 'red' }}>NON-MVP</span>
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="body2">When do progress notes need to be signed by?</Typography>
              <TextField
                value={progressNoteTime}
                onChange={e => setProgressNoteTime(e.target.value)}
                size="small"
                sx={{ width: 100 }}
                helperText={<span style={{ color: 'red' }}>NON-MVP</span>}
              />
            </Stack>
          </Box>
          */}
        </Stack>
      </Paper>

      <Typography variant="body1" paragraph>
        Enable or disable audit modules to customize your compliance checks.
      </Typography>

      <Box sx={{ mt: 2 }}>
        <MeasureTable
          rows={auditConfig.measures}
          onChange={(measures) => onChangeAuditConfig({ ...auditConfig, measures })}
        />
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Delete this configuration?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. Are you sure you want to delete this audit configuration?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} data-qa-id="config-delete-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            data-qa-id="config-delete-confirm-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default AuditConfigEdit
